import { useMemo, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { ClipboardList } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import DataPagination from '@/shared/ui/DataPagination'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  historyInstrumentTaskTaskLogPath,
  instrumentPath,
  instrumentsPath,
  newInstrumentTaskTaskLogPath,
} from '@/shared/lib/routes'
import { CommentBody } from '@/features/comments/CommentsPanel'
import { TaskStatusBadge } from '@/features/task-status'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type {
  WebInstrumentsInstrumentRef,
  WebInstrumentsInstrumentTaskRef,
  WebInstrumentsTaskLogHistoryRow,
} from '@/shared/types/web'
import type { SharedProps } from '@/shared/types/SharedProps'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

type HistoryFilters = {
  status: string
  loggedFrom: string
  loggedTo: string
}

type HistoryPageProps = {
  instrument: WebInstrumentsInstrumentRef
  instrumentTask: WebInstrumentsInstrumentTaskRef
  rows: WebInstrumentsTaskLogHistoryRow[]
  hasActiveTaskLog: boolean
  filters: HistoryFilters
  pagination: PaginationMeta
}

const HistoryPerformedAtCell = ({
  row,
}: {
  row: WebInstrumentsTaskLogHistoryRow
  tenantTimeFormat: SharedProps['tenantTimeFormat']
}) => {
  const { formatDateTime } = useSiteDateTime()
  const performedAt = row.completedAtLocal ?? row.loggedAtLocal
  if (!performedAt) return <span className="text-muted-foreground">—</span>

  const showSubmittedSecondary =
    row.dateModified && row.loggedAtLocal && row.loggedAtLocal !== row.completedAtLocal
  const submittedAt = showSubmittedSecondary ? row.loggedAtLocal : null

  return (
    <div className="space-y-0.5">
      <div className="whitespace-nowrap">
        {formatDateTime(performedAt)}
        {row.dateModified && (
          <span className="ml-1 text-xs text-amber-700">
            {t('instruments.reports.cell.dateModifiedSuffix')}
          </span>
        )}
      </div>
      {submittedAt && (
        <p className="text-xs text-muted-foreground">
          {t('instruments.taskLogHistory.submittedAt')}: {formatDateTime(submittedAt)}
        </p>
      )}
    </div>
  )
}

const HistoryCommentsCell = ({ comments }: { comments: string | null }) => {
  if (!comments) return <span className="text-muted-foreground">—</span>
  return (
    <div className="max-w-xs overflow-hidden">
      <CommentBody body={comments} className="text-sm [&_p]:my-0" />
    </div>
  )
}

const HistoryPage = () => {
  const { formatDateTime } = useSiteDateTime()
  const page = usePage<SharedProps & HistoryPageProps>()
  const { tenantTimeFormat, instrument, instrumentTask, rows, filters, pagination } = page.props
  useBreadcrumbs([
    { label: t('nav.instruments'), href: instrumentsPath() },
    { label: instrument.instrumentLabel, href: instrumentPath(instrument.id) },
    { label: t('instruments.show.tabs.tasks'), href: `${instrumentPath(instrument.id)}#tasks` },
    { label: 'History' },
  ])
  const canCreateTaskLog = useCan(Resources.INSTRUMENT_TASK_LOG, Actions.CREATE)
  const basePath = useMemo(
    () => historyInstrumentTaskTaskLogPath(instrument.id, instrumentTask.id),
    [instrument.id, instrumentTask.id],
  )

  const [status, setStatus] = useState(filters.status)
  const [loggedFrom, setLoggedFrom] = useState(filters.loggedFrom)
  const [loggedTo, setLoggedTo] = useState(filters.loggedTo)

  const navigate = (overrides: Record<string, string | undefined> = {}) => {
    const s = overrides.status ?? status
    const lf = overrides.loggedFrom ?? loggedFrom
    const lt = overrides.loggedTo ?? loggedTo

    const q: Record<string, string> = { status: s }
    if (lf) q.logged_from = lf
    if (lt) q.logged_to = lt
    router.get(basePath, q, { preserveScroll: true, replace: true })
  }

  const handleStatusChange = (val: string) => {
    setStatus(val)
    navigate({ status: val })
  }

  const handleLoggedFromChange = (val: string) => {
    setLoggedFrom(val)
    navigate({ loggedFrom: val })
  }

  const handleLoggedToChange = (val: string) => {
    setLoggedTo(val)
    navigate({ loggedTo: val })
  }

  const pageUrl = (p: number) => {
    const params = new URLSearchParams()
    params.set('status', filters.status)
    if (filters.loggedFrom) params.set('logged_from', filters.loggedFrom)
    if (filters.loggedTo) params.set('logged_to', filters.loggedTo)
    params.set('page', String(p))
    return `${basePath}?${params.toString()}`
  }

  const goToPage = (p: number) => router.get(pageUrl(p), {}, { preserveScroll: true })

  const VoidAuditCell = ({ row }: { row: WebInstrumentsTaskLogHistoryRow }) => {
    if (row.active) return <span className="text-muted-foreground">—</span>
    if (row.voidAuditPending)
      return (
        <span className="text-muted-foreground text-sm">
          {t('instruments.taskLogHistory.auditPending')}
        </span>
      )
    return (
      <div className="space-y-0.5 text-sm">
        {row.voidDeleteReason && (
          <p>
            <span className="text-muted-foreground">
              {t('instruments.taskLogHistory.voidReason')}:{' '}
            </span>
            {row.voidDeleteReason}
          </p>
        )}
        {row.voidDeletedByLabel && (
          <p>
            <span className="text-muted-foreground">
              {t('instruments.taskLogHistory.voidBy')}:{' '}
            </span>
            {row.voidDeletedByLabel}
          </p>
        )}
        {row.voidDeletedAtLocal && (
          <p>
            <span className="text-muted-foreground">
              {t('instruments.taskLogHistory.voidAt')}:{' '}
            </span>
            {formatDateTime(row.voidDeletedAtLocal)}
          </p>
        )}
      </div>
    )
  }

  const RowActions = ({ row }: { row: WebInstrumentsTaskLogHistoryRow }) => (
    <div className="flex flex-wrap gap-2">
      <Button asChild variant="secondary" size="sm">
        <a href={row.pdfHref} target="_blank" rel="noreferrer">
          {t('instruments.taskLogHistory.actionPdf')}
        </a>
      </Button>
    </div>
  )

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold sm:text-2xl">{t('instruments.taskLogHistory.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {instrument.instrumentLabel} — {instrumentTask.name}
            {instrumentTask.inactive && (
              <span className="ml-1 text-muted-foreground">
                {t('instruments.taskLogHistory.inactiveSuffix')}
              </span>
            )}
          </p>
        </div>
        {canCreateTaskLog && (
          <Button asChild className="shrink-0">
            <Link href={newInstrumentTaskTaskLogPath(instrument.id, instrumentTask.id)}>
              {t('instruments.taskLogHistory.addLog')}
            </Link>
          </Button>
        )}
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="status">{t('instruments.taskLogHistory.filterStatus')}</Label>
            <Select value={status} onValueChange={handleStatusChange}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('instruments.taskLogHistory.statusAll')}</SelectItem>
                <SelectItem value="active">
                  {t('instruments.taskLogHistory.statusActive')}
                </SelectItem>
                <SelectItem value="voided">
                  {t('instruments.taskLogHistory.statusVoided')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="loggedFrom">{t('instruments.taskLogHistory.filterLoggedFrom')}</Label>
            <DatePickerInput
              id="loggedFrom"
              value={loggedFrom}
              onChange={handleLoggedFromChange}
              allowTyping
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="loggedTo">{t('instruments.taskLogHistory.filterLoggedTo')}</Label>
            <DatePickerInput
              id="loggedTo"
              value={loggedTo}
              onChange={handleLoggedToChange}
              allowTyping
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('instruments.taskLogHistory.filterDateHelp')}
        </p>
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <ClipboardList className="h-5 w-5 text-muted-foreground" />
            </span>
            <p className="text-sm text-muted-foreground">{t('instruments.taskLogHistory.empty')}</p>
          </div>
        ) : (
          rows.map((row) => (
            <div
              key={row.id}
              className={`rounded-lg border bg-card p-3 shadow-sm space-y-2 ${!row.active ? 'border-muted bg-muted/20' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <HistoryPerformedAtCell row={row} tenantTimeFormat={tenantTimeFormat} />
                <div className="flex items-center gap-1.5 shrink-0">
                  {row.statusCode && <TaskStatusBadge taskStatusCode={row.statusCode} />}
                  {!row.active && (
                    <span className="text-xs text-muted-foreground">
                      ({t('instruments.taskLogHistory.badgeVoided')})
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div>
                  <span className="text-muted-foreground">
                    {t('instruments.taskLogHistory.colQc')}:{' '}
                  </span>
                  {row.qcPassed
                    ? t('instruments.taskLogHistory.qcYes')
                    : t('instruments.taskLogHistory.qcNo')}
                </div>
                <div className="truncate">
                  <span className="text-muted-foreground">
                    {t('instruments.taskLogHistory.colCompletedBy')}:{' '}
                  </span>
                  {row.completedByLabel ?? '—'}
                </div>
              </div>

              {row.comments && (
                <div className="text-sm text-muted-foreground">
                  <HistoryCommentsCell comments={row.comments} />
                </div>
              )}

              {!row.active && (
                <div className="border-t pt-2">
                  <VoidAuditCell row={row} />
                </div>
              )}

              <div className="border-t pt-2">
                <RowActions row={row} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('instruments.taskLogHistory.colCompletedAt')}</TableHead>
              <TableHead>{t('instruments.taskLogHistory.colStatus')}</TableHead>
              <TableHead>{t('instruments.taskLogHistory.colQc')}</TableHead>
              <TableHead>{t('instruments.taskLogHistory.colCompletedBy')}</TableHead>
              <TableHead>{t('instruments.taskLogHistory.colComments')}</TableHead>
              <TableHead>{t('instruments.taskLogHistory.colVoidAudit')}</TableHead>
              <TableHead className="text-right">
                {t('instruments.taskLogHistory.colActions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                      <ClipboardList className="h-5 w-5 text-muted-foreground" />
                    </span>
                    <p className="text-sm text-muted-foreground">
                      {t('instruments.taskLogHistory.empty')}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} className={!row.active ? 'bg-muted/20' : undefined}>
                  <TableCell className="text-sm">
                    <HistoryPerformedAtCell row={row} tenantTimeFormat={tenantTimeFormat} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {row.statusCode ? (
                        <TaskStatusBadge taskStatusCode={row.statusCode} />
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                      {!row.active && (
                        <span className="text-muted-foreground text-xs">
                          ({t('instruments.taskLogHistory.badgeVoided')})
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {row.qcPassed
                      ? t('instruments.taskLogHistory.qcYes')
                      : t('instruments.taskLogHistory.qcNo')}
                  </TableCell>
                  <TableCell className="text-sm">{row.completedByLabel ?? '—'}</TableCell>
                  <TableCell className="text-sm">
                    <HistoryCommentsCell comments={row.comments} />
                  </TableCell>
                  <TableCell className="text-sm">
                    <VoidAuditCell row={row} />
                  </TableCell>
                  <TableCell className="text-right">
                    <RowActions row={row} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <DataPagination
        meta={pagination}
        onPageChange={goToPage}
        noun={t('instruments.taskLogHistory.records')}
      />
    </div>
  )
}

export default HistoryPage
